import { useMemo } from 'react'
import type { LanguageCode } from '@sevenrooms/api'
import { useMaxWidthBreakpoint } from '@sevenrooms/ui-kit/hooks'
import { WidgetContainer } from '../../components/WidgetContainer'
import {
  useAppLoading,
  useVenue,
  useWidgetLanguage,
  useWidgetSettings,
  useGetSevenroomsLogin,
  useReservationsRoute,
  useGroupOrCrossSellWidget,
} from '../../hooks'
import { useAppDispatch, updateFormState } from '../../store'
import { Search } from './Search'
import { SearchContainer } from './SearchContainer'
import { SearchLoader } from './SearchLoader'

interface SearchAndAvailabilityProps {
  shouldDisplayLoader: boolean
  isModifyRoute?: boolean
}
export function SearchAndAvailability({ shouldDisplayLoader, isModifyRoute }: SearchAndAvailabilityProps) {
  const { availableLanguageOptions, selectedLanguage, setSelectedLanguage } = useWidgetLanguage()
  const isLoading = useAppLoading()
  const dispatch = useAppDispatch()
  const { largeLogoUrl, name, newResWidgetPrivateEventsEnabled, countOfPrivateEvents, newResWidgetExperiencesEnabled, countOfExperiences } =
    useVenue()
  const { headerImgUrl, headerImgMobileUrl } = useWidgetSettings()
  const { linkToDefaultCreateReservationWidget } = useReservationsRoute()
  const isMobile = useMaxWidthBreakpoint('s')
  const finalHeaderImgUrl = useMemo(() => {
    if (isModifyRoute) {
      return undefined
    }
    if (isMobile && headerImgMobileUrl) {
      return headerImgMobileUrl
    }
    return headerImgUrl
  }, [isModifyRoute, isMobile, headerImgUrl, headerImgMobileUrl])
  const languagePicker = useMemo(() => {
    if (availableLanguageOptions.length <= 1) {
      return undefined
    }
    const handleSetSelectedLanguage = (language: LanguageCode) => {
      setSelectedLanguage(language)
      dispatch(updateFormState({ preferredLanguage: language }))
    }
    return {
      value: selectedLanguage,
      onChange: handleSetSelectedLanguage,
      options: availableLanguageOptions,
      disabled: isLoading,
    }
  }, [availableLanguageOptions, dispatch, isLoading, selectedLanguage, setSelectedLanguage])

  const { hideTabs, devMode } = useReservationsRoute()
  const { isGroupWidget } = useGroupOrCrossSellWidget()

  const showPrivateEvents = newResWidgetPrivateEventsEnabled && (isGroupWidget || !!countOfPrivateEvents)
  const showExperiences = (!!newResWidgetExperiencesEnabled && !!countOfExperiences) || devMode

  // For now tabs are not available for modify route and group widget without private events
  const showTabs = !hideTabs && !isModifyRoute && !(isGroupWidget && !showPrivateEvents)

  const sevenRoomsLogin = useGetSevenroomsLogin()

  return (
    <WidgetContainer
      largeLogoUrl={largeLogoUrl}
      venueLink={linkToDefaultCreateReservationWidget}
      venueName={name}
      headerImageUrl={finalHeaderImgUrl}
      breadcrumbsPortalId="search-breadcrumbs"
      enableGrowingTopNav={!showTabs && !isModifyRoute}
      enableGrowingBreadcrumbs={isModifyRoute}
      showGutters
      languagePicker={languagePicker}
      sevenRoomsLogin={sevenRoomsLogin}
    >
      <SearchContainer>
        {shouldDisplayLoader ? (
          <SearchLoader isModifyRoute={isModifyRoute || false} />
        ) : (
          <Search
            isModifyRoute={isModifyRoute}
            showTabs={showTabs}
            showPrivateEvents={showPrivateEvents}
            showExperiences={showExperiences}
          />
        )}
      </SearchContainer>
    </WidgetContainer>
  )
}

export * from './useSearchForm'
